import { useState, type ChangeEvent, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { Textarea } from '@/components/Textarea';
import usePatchProfitMemo from '../api/usePatchProfitMemo';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';

type Props = {
  profitId: number;
  memo: string;
};

/**
 * マッチング管理の JobChangeProcessNote と同様、枠内表示・クリックで編集・デバウンス保存
 */
export default function ProfitCardMemo({ profitId, memo }: Props) {
  const debounce = useDebounce(500);
  const { mutateAsync } = usePatchProfitMemo();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(memo);

  useEffect(() => {
    setValue(memo);
  }, [memo]);

  const onChangeMemo = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.currentTarget.value;
    setValue(next);

    debounce(async () => {
      try {
        await mutateAsync({ id: profitId, memo: next });
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'メモの更新に失敗しました');
      }
    });
  };

  return (
    <div className="overflow-hidden">
      {!isEditing ? (
        <div className="h-[62px] max-h-[62px] rounded border border-solid border-border bg-white p-2">
          <div
            className="line-clamp-2 h-[39px] cursor-pointer overflow-hidden text-xs leading-[170%] tracking-normal break-words text-[#262626]"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            {value.trim() !== '' ? value : null}
          </div>
        </div>
      ) : (
        <Textarea
          className="h-[62px] min-h-[62px] w-full resize-none border-2 border-border p-2 text-xs leading-[170%] tracking-normal"
          name="profit_memo"
          id={`profit_memo_${profitId}`}
          value={value}
          maxLength={1000}
          onChange={(e) => {
            e.stopPropagation();
            onChangeMemo(e);
          }}
          onBlur={() => setIsEditing(false)}
          onClick={(e) => {
            e.stopPropagation();
          }}
          autoFocus
        />
      )}
    </div>
  );
}
